/**
 * Merlin order cards — pure extraction logic (no React).
 *
 * Lifts the two order-placement tool frames into card models, exactly like the
 * `voice_reply` → spoken-rundown lift:
 *
 * - `propose_order_readback` (tool_use INPUT) → the READ-BACK card: the whole
 *   order at a glance with the unresolved questions highlighted and a spoken
 *   summary for the card's speak button. The tool chip is dropped from the
 *   visible trace — the card IS its rendering.
 * - `create_draft_order` (tool_result SUMMARY, success only) → the DRAFT card:
 *   order id + NOT-SUBMITTED state + the Review deep link. Recognized by the
 *   result-text signature so the reducer needs no tool_use_id bookkeeping; the
 *   chip stays in the trace (admins see the full input there).
 */

import type { AssistantBlock, ToolUseEvent } from '../services/agentConsole';

export interface ReadbackLine {
  itemCode: string | null;
  description: string | null;
  quantity: number | null;
  unit: string | null;
  unitPrice: number | null;
  needsReview: boolean;
}

export interface OrderReadbackData {
  customerName: string | null;
  customerNo: string | null;
  poNumber: string | null;
  lines: ReadbackLine[];
  computedSubtotal: number | null;
  unresolved: string[];
  spokenSummary: string;
}

export interface DraftCardData {
  orderId: string;
  /** Fail-closed drafts: every line held for the orders team's answer. */
  held: boolean;
  lineCount: number | null;
  flaggedCount: number | null;
}

export function isOrderReadbackTool(name: string): boolean {
  return name === 'propose_order_readback' || name.endsWith('__propose_order_readback');
}

export function isCreateDraftOrderTool(name: string): boolean {
  return name === 'create_draft_order' || name.endsWith('__create_draft_order');
}

interface RawLine {
  item_code?: unknown;
  product_description?: unknown;
  notes?: unknown;
  quantity_ordered?: unknown;
  unit?: unknown;
  unit_price?: unknown;
  needs_review?: unknown;
}

interface RawReadbackInput {
  order?: { customer_no?: unknown; customer_po_no?: unknown; line_items?: unknown };
  customer_name?: unknown;
  computed_subtotal?: unknown;
  unresolved?: unknown;
  spoken_summary?: unknown;
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

/** Build the read-back card model from the tool input; null when malformed. */
export function readbackFromInput(input: Record<string, unknown>): OrderReadbackData | null {
  const raw = input as RawReadbackInput;
  const order = raw.order;
  if (!order || typeof order !== 'object') return null;
  const rawLines = Array.isArray(order.line_items) ? (order.line_items as RawLine[]) : [];
  const lines = rawLines.map<ReadbackLine>((li) => ({
    itemCode: str(li.item_code),
    description: str(li.product_description) ?? str(li.notes),
    quantity: num(li.quantity_ordered),
    unit: str(li.unit),
    unitPrice: num(li.unit_price),
    needsReview: li.needs_review === true,
  }));
  const unresolved = Array.isArray(raw.unresolved)
    ? raw.unresolved.filter((u): u is string => typeof u === 'string' && !!u.trim())
    : [];
  return {
    customerName: str(raw.customer_name),
    customerNo: str(order.customer_no),
    poNumber: str(order.customer_po_no),
    lines,
    computedSubtotal: num(raw.computed_subtotal),
    unresolved,
    spokenSummary: str(raw.spoken_summary) ?? '',
  };
}

export function readbackFromToolUse(evt: ToolUseEvent): OrderReadbackData | null {
  return readbackFromInput(evt.input ?? {});
}

// Mirrors the success text emitted by create_draft_order (orders_draft.py):
// "Draft order <id> created (NOT submitted).[ HELD FOR REVIEW: …] N line(s),
//  M flagged for review. … deep link: /portal/orders?orderId=<id>. …"
const DRAFT_RESULT_RE =
  /Draft order ([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}) created \(NOT submitted\)/i;
const COUNTS_RE = /(\d+)\s+line\(s\),\s+(\d+)\s+flagged/i;

/** Build the draft card from a create_draft_order SUCCESS result; else null. */
export function draftCardFromResultSummary(summary: string | null | undefined): DraftCardData | null {
  if (!summary) return null;
  const match = DRAFT_RESULT_RE.exec(summary);
  if (!match) return null;
  const counts = COUNTS_RE.exec(summary);
  return {
    orderId: match[1],
    held: /HELD FOR REVIEW/.test(summary),
    lineCount: counts ? Number(counts[1]) : null,
    flaggedCount: counts ? Number(counts[2]) : null,
  };
}

export interface LiftedOrderCards {
  blocks: AssistantBlock[];
  readback: OrderReadbackData | null;
  draftCard: DraftCardData | null;
}

/**
 * Resume-path lift: pull the cards out of a saved turn's reconstructed blocks.
 * The read-back chip is dropped (the card replaces it); the create chip stays.
 * Keeps the LAST occurrence of each, matching the live reducer's latest-wins.
 */
export function liftOrderCards(blocks: AssistantBlock[]): LiftedOrderCards {
  let readback: OrderReadbackData | null = null;
  let draftCard: DraftCardData | null = null;
  const kept: AssistantBlock[] = [];
  for (const block of blocks) {
    if (block.kind === 'tool') {
      const { rawName, input, resultSummary, status } = block.tool;
      if (isOrderReadbackTool(rawName)) {
        if (status !== 'error') readback = readbackFromInput(input) ?? readback;
        continue; // card replaces the chip
      }
      if (isCreateDraftOrderTool(rawName)) {
        draftCard = draftCardFromResultSummary(resultSummary) ?? draftCard;
      }
    }
    kept.push(block);
  }
  return { blocks: kept, readback, draftCard };
}
